/**
 * @theme/media-gallery — PDP image/video gallery with thumbnails + main viewer.
 *
 * Behavior preserved:
 *   - Listens for `slideChanged` (custom event from slider) on the viewer; updates active thumb.
 *   - Click on a thumbnail → setActiveMedia(mediaId).
 *   - For variant filtering: hide thumbs/slides whose `data-alt` doesn't match current variant option,
 *     except those marked `data-alt="always_display"`.
 *   - On active media change: pause-all, then load deferred media, scroll into view, announce via
 *     live region, prevent sticky header reveal.
 *   - On desktop with thumbnail layout: remove list-semantic ARIA (set roles to "presentation").
 */

import { Component, defineComponent } from '@theme/component';
import { debounce, pauseAllMedia } from '@theme/utilities';

/**
 * @extends {Component}
 */
export class MediaGallery extends Component {
  /** @type {{ liveRegion: HTMLElement | null, viewer: HTMLElement | null, thumbnails: HTMLElement | null }} */
  elements = { liveRegion: null, viewer: null, thumbnails: null };
  /** @type {MediaQueryList | null} */ mql = null;
  /** @type {HTMLElement | null} */ productInfo = null;
  /** @type {HTMLElement | null} */ stickyHeader = null;
  prependMedia = true;
  filteringOption = '';

  connectedCallback() {
    super.connectedCallback();
    this.elements = {
      liveRegion: this.querySelector('[id^="GalleryStatus"]'),
      viewer: this.querySelector('[id^="GalleryViewer"]'),
      thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
    };
    this.mql = window.matchMedia('(min-width: 750px)');
    this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
    this.prependMedia = this.dataset.disablePrepend !== 'true';
    /** @type {any} */ (this.productInfo)?.initShareLinks?.();
    this.filteringOption = this.dataset.filteringOption ?? '';
    if (!this.elements.thumbnails) return;

    this.elements.viewer?.addEventListener('slideChanged', debounce(this.#onSlideChanged, 500));
    this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumb) => {
      thumb.querySelector('button')?.addEventListener('click', () => {
        this.setActiveMedia(/** @type {HTMLElement} */ (thumb).dataset.target ?? '', false);
      });
    });
    if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.#removeListSemantic();
  }

  /** @param {Event} event */
  #onSlideChanged = (event) => {
    /** @type {any} */
    const detail = /** @type {any} */ (event).detail;
    const mediaId = detail?.currentElement?.dataset?.mediaId;
    if (!mediaId) return;
    /** @type {HTMLElement | null} */
    const thumb = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`) ?? null;
    if (thumb) this.#setActiveThumbnail(thumb);
  };

  /**
   * @param {string} mediaId
   * @param {boolean} prepend
   * @param {boolean} [filtering=false]
   * @param {any} [currentVariant=null]
   */
  setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
    if (filtering && currentVariant && this.filteringOption) {
      const filterValue = currentVariant[this.filteringOption];
      this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link').forEach((slide) => {
        const alt = /** @type {HTMLElement} */ (slide).dataset.alt;
        slide.classList.toggle('hidden', !(alt === filterValue || alt === 'always_display'));
      });
    }

    /** @type {HTMLElement | null} */
    const activeMedia = this.elements.viewer?.querySelector(`[data-media-id="${mediaId}"]`) ?? null;
    if (!activeMedia) return;
    this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
    activeMedia.classList.add('is-active');

    if (prepend && this.prependMedia) {
      activeMedia.parentElement?.prepend(activeMedia);
      const activeThumb = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
      activeThumb?.parentElement?.prepend(activeThumb);
      /** @type {any} */ (this.elements.viewer)?.resetPages?.();
    }

    this.#preventStickyHeader();
    setTimeout(() => {
      if (this.elements.thumbnails) {
        activeMedia.parentElement?.scrollTo({ left: activeMedia.offsetLeft });
      }
      if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
        activeMedia.scrollIntoView({ behavior: 'smooth' });
      }
    });
    this.#playActiveMedia(activeMedia);

    if (filtering && currentVariant) {
      /** @type {any} */ (this.elements.viewer)?.initPages?.();
      /** @type {any} */ (this.elements.viewer)?.update?.();
    }

    if (!this.elements.thumbnails) return;
    /** @type {HTMLElement | null} */
    const thumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
    if (thumb) {
      this.#setActiveThumbnail(thumb);
      this.#announceLiveRegion(activeMedia, thumb.dataset.mediaPosition ?? '');
    }
  }

  /** @param {HTMLElement} thumbnail */
  #setActiveThumbnail(thumbnail) {
    if (!this.elements.thumbnails || !thumbnail) return;
    this.elements.thumbnails.querySelectorAll('button').forEach((b) => b.removeAttribute('aria-current'));
    thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');
    /** @type {any} */
    const slider = this.elements.thumbnails;
    if (slider.isSlideVisible?.(thumbnail, false, 10, true)) return;
    if (slider.vertical) slider.slider?.scrollTo({ top: thumbnail.offsetTop });
    else slider.slider?.scrollTo({ left: thumbnail.offsetLeft });
  }

  /**
   * @param {HTMLElement} activeItem
   * @param {string} position
   */
  #announceLiveRegion(activeItem, position) {
    /** @type {HTMLImageElement | null} */
    const image = activeItem.querySelector('.product__modal-opener--image img');
    if (!image || !this.elements.liveRegion) return;
    const liveRegion = this.elements.liveRegion;
    image.onload = () => {
      liveRegion.setAttribute('aria-hidden', 'false');
      const tmpl = /** @type {any} */ (window).accessibilityStrings?.imageAvailable ?? '';
      liveRegion.innerHTML = tmpl.replace('[index]', position);
      setTimeout(() => liveRegion.setAttribute('aria-hidden', 'true'), 2000);
    };
    image.src = image.src;
  }

  /** @param {HTMLElement} activeItem */
  #playActiveMedia(activeItem) {
    pauseAllMedia();
    /** @type {any} */
    const deferred = activeItem.querySelector('.deferred-media');
    deferred?.loadContent?.(false);
  }

  #preventStickyHeader() {
    if (!this.stickyHeader) this.stickyHeader = document.querySelector('sticky-header');
    this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
  }

  #removeListSemantic() {
    /** @type {any} */
    const slider = this.elements.viewer;
    if (!slider?.slider) return;
    slider.slider.setAttribute('role', 'presentation');
    slider.sliderItems?.forEach((/** @type {HTMLElement} */ slide) => slide.setAttribute('role', 'presentation'));
  }
}

defineComponent('media-gallery', MediaGallery);
